import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Auth
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/auth/LoginPage.vue'),
      meta: { requiresAuth: false },
    },
    // Choix du mot de passe (lien reçu par email : invitation ou mot de passe oublié)
    {
      path: '/mot-de-passe',
      name: 'password',
      component: () => import('@/pages/auth/PasswordPage.vue'),
      meta: { requiresAuth: false },
    },
    // App utilisateur (protégée)
    {
      path: '/',
      component: () => import('@/components/layout/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/pages/DashboardPage.vue'),
        },
        {
          path: 'templates',
          name: 'templates',
          component: () => import('@/pages/TemplatesPage.vue'),
        },
        {
          path: 'editor',
          name: 'editor',
          component: () => import('@/pages/EditorPage.vue'),
        },
        {
          path: 'visuals',
          name: 'visuals',
          component: () => import('@/pages/VisualsPage.vue'),
        },
        {
          path: 'calendar',
          name: 'calendar',
          component: () => import('@/pages/CalendarPage.vue'),
        },
        {
          // Mode Match : les templates du jour de match (accès rapide, mobile).
          path: 'match',
          name: 'match',
          component: () => import('@/pages/MatchPage.vue'),
        },
        {
          path: 'library',
          name: 'library',
          component: () => import('@/pages/LibraryPage.vue'),
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('@/pages/ProfilePage.vue'),
        },
        {
          // Ancien assistant vidéo — remplacé par l'éditeur unifié (templates vidéo
          // ouverts via /editor?video=1). On redirige les éventuels favoris.
          path: 'video',
          redirect: { name: 'templates' },
        },
      ],
    },
    // Administration (protégée + admin uniquement)
    {
      path: '/admin',
      component: () => import('@/pages/admin/AdminLayout.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
      children: [
        {
          path: '',
          name: 'admin',
          component: () => import('@/pages/admin/AdminDashboard.vue'),
        },
        {
          path: 'templates',
          name: 'admin-templates',
          component: () => import('@/pages/admin/TemplatesPage.vue'),
        },
        {
          path: 'templates/new',
          name: 'admin-template-new',
          component: () => import('@/pages/admin/TemplateEditPage.vue'),
        },
        {
          path: 'templates/:id',
          name: 'admin-template-edit',
          component: () => import('@/pages/admin/TemplateEditPage.vue'),
        },
        {
          path: 'templates/:id/layers',
          name: 'admin-template-layers',
          component: () => import('@/pages/admin/TemplateLayersPage.vue'),
        },
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('@/pages/admin/UsersPage.vue'),
        },
        {
          path: 'organizations',
          name: 'admin-organizations',
          component: () => import('@/pages/admin/OrganizationsPage.vue'),
        },
        {
          path: 'teams',
          name: 'admin-teams',
          component: () => import('@/pages/admin/TeamsPage.vue'),
        },
        {
          path: 'teams/:teamId/players',
          name: 'admin-team-players',
          component: () => import('@/pages/admin/PlayersPage.vue'),
        },
        {
          path: 'organizers',
          name: 'admin-organizers',
          component: () => import('@/pages/admin/OrganizersPage.vue'),
        },
        {
          // Onglet « Drivers » fondu dans « Personnes » — ancien lien redirigé.
          path: 'drivers',
          redirect: { name: 'admin-persons' },
        },
        {
          path: 'persons',
          name: 'admin-persons',
          component: () => import('@/pages/admin/PersonnesPage.vue'),
        },
        {
          path: 'competitions',
          name: 'admin-competitions',
          component: () => import('@/pages/admin/CompetitionsPage.vue'),
        },
        {
          path: 'participants',
          name: 'admin-participants',
          component: () => import('@/pages/admin/ClubsPage.vue'),
        },
        {
          path: 'clubs',
          redirect: { name: 'admin-participants' },
        },
        {
          path: 'reporting',
          name: 'admin-reporting',
          component: () => import('@/pages/admin/ReportingPage.vue'),
        },
        {
          path: 'fonts',
          name: 'admin-fonts',
          component: () => import('@/pages/admin/FontsPage.vue'),
        },
        {
          path: 'video-templates',
          name: 'admin-video-templates',
          component: () => import('@/pages/admin/VideoTemplatesPage.vue'),
        },
        {
          path: 'video-templates/:id/layers',
          name: 'admin-video-template-layers',
          component: () => import('@/pages/admin/TemplateLayersPage.vue'),
        },
      ],
    },
    // Rendu headless (navigateur fantôme) : s'authentifie via un jeton puis
    // ouvre l'éditeur. Pas de garde d'auth : la page gère elle-même la connexion.
    {
      path: '/render',
      name: 'render',
      component: () => import('@/pages/RenderPage.vue'),
      meta: { requiresAuth: false },
    },
    // Fallback
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
})

// Guard : auth + rôle admin
router.beforeEach(async (to) => {
  const authStore = useAuthStore()

  if (authStore.loading) {
    await authStore.init()
  }

  // Redirige vers /login si non connecté
  if (to.meta.requiresAuth !== false && !authStore.isAuthenticated) {
    return { name: 'login' }
  }

  // Redirige vers / si connecté mais pas admin (tentative d'accès /admin)
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    return { name: 'dashboard' }
  }

  // Redirige vers / si déjà connecté
  if (to.name === 'login' && authStore.isAuthenticated) {
    return { name: 'dashboard' }
  }
})

export default router
